import * as ExifReader from "exifreader";
import * as crypto from "crypto";
import Evaporate, { TransferStats } from "evaporate";
import { getKey } from "../../../../../../smokepit/src/utils/s3keys";

const cryptoMd5Method = (data) =>
  crypto
    .createHash("md5")
    .update(new Buffer(new Uint8Array(data)))
    .digest("base64");

const cryptoHexEncodedHash256 = (data) =>
  crypto.createHash("sha256").update(data).digest("hex");

const completeImage = (surveyId: number, id: number | undefined) => {
  if (!id) throw new Error("Cannot complete an unregistered image asset");

  return fetch(`/api/v1/surveys/${surveyId}/images/${id}`, {
    method: "PUT",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uploaded: true,
    }),
  }).then((response) => response.json());
};

const filterExif = (exif) => {
  return {
    DateTimeOriginal: exif.DateTimeOriginal,
    GPSLatitude: exif.GPSLatitude,
    GPSLatitudeRef: exif.GPSLatitudeRef,
    GPSLongitude: exif.GPSLongitude,
    GPSLongitudeRef: exif.GPSLongitudeRef,
  };
};

export const createImages = (context: UploadContext, event) => (callback) => {
  const size = Math.floor(Math.random() * 60) + 60;
  const total = context.files.length;
  const batch = {
    total,
    size: size < total ? size : total,
    count: 0,
    complete: 0,
  };

  callback({ type: "START", total: context.files.length });

  return Promise.all(
    context.files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = (event) => {
            try {
              batch.count += 1;
              batch.complete += 1;

              if (batch.count >= batch.size) {
                callback({ type: "UPDATE", total: batch.count });

                if (batch.total - batch.complete < batch.size) {
                  batch.size = batch.total - batch.complete;
                } else {
                  batch.size = Math.floor(Math.random() * 60) + 60;
                }

                batch.count = 0;
              }

              resolve({
                file,
                exif: filterExif(ExifReader.load(event.target.result)),
                uploaded: false,
              });
            } catch (error) {
              reject(error);
            }
          };

          reader.readAsArrayBuffer(file);
        })
    )
  );
};

const createS3Uploader =
  () => (context: UploadContext, event) => (callback) => {
    return Evaporate.create({
      ...event.data,
      maxConcurrentParts: 5,
      partSize: 20 * 1024 * 1024,
      computeContentMd5: true,
      cryptoMd5Method,
      cryptoHexEncodedHash256,
      logging: true,
      progressIntervalMS: 500,
    }).then((evaporate: Evaporate) => {
      callback({
        type: "REGISTER_CANCEL",
        cancel: evaporate.cancel.bind(evaporate),
      });
      return Promise.all(
        Object.entries(context.images)
          .filter(([key, image]) => image.uploaded === false && image.handle)
          .map(([key, image]) =>
            evaporate.add({
              name: getKey({
                key,
                surveyId: context.surveyId,
                orderId: context.orderId,
                folder: "images/rgb",
              }),
              file: (image as Image).handle,
              complete: (
                xhr: XMLHttpRequest,
                awsObjectKey: string,
                stats: TransferStats
              ) => {
                completeImage(context.surveyId, image.id).then(() =>
                  callback({ type: "FILE_COMPLETE", id: key, stats })
                );
              },
              progress: (progress: number, stats: TransferStats) => {
                if (progress < 1) {
                  callback({
                    type: "FILE_PROGRESS",
                    id: key,
                    progress,
                    stats,
                  });
                }
              },
              error: (message) => console.log("evaporate error ", message),
            })
          )
      );
    });
  };

export const s3ImageUploader = createS3Uploader();
